import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Send, MapPin, Mail, Phone } from 'lucide-react';

export default function CTA() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', property: '', message: '' });

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing details', description: 'Please add your name and email so we can reach you.' });
      return;
    }
    // Save to local storage as a mock backend
    const saved = JSON.parse(localStorage.getItem('uw_demos') || '[]');
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('uw_demos', JSON.stringify(saved));
    toast({ title: 'Demo request received', description: 'Our hospitality team will reach out within 24 hours.' });
    setForm({ name: '', email: '', phone: '', property: '', message: '' });
  };

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-b from-white to-sky-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs tracking-[0.3em] text-sky-600 font-semibold uppercase mb-3">Book a Free Demo</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 leading-tight">
              Ready to <span className="water-text">upgrade</span> your water?
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
              Tell us about your property or vessel and we’ll design a system that matches its footprint,
              guest volume and aesthetic. On-site demo available across major hospitality hubs.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: MapPin, title: 'Head Office', text: 'Mumbai, India • Global service partners' },
                { icon: Mail, title: 'Email', text: 'hospitality@upgradewater.com' },
                { icon: Phone, title: 'Concierge line', text: '+91 1800 UPGRADE (877-4237)' }
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.title} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-white border border-sky-200 flex items-center justify-center shadow-sm">
                      <Icon className="w-4 h-4 text-sky-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 tracking-wide">{it.title}</div>
                      <div className="text-sm font-medium text-slate-900">{it.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl border border-sky-100 shadow-xl shadow-sky-100/50 p-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600 font-medium">Full name</label>
                <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Sofia Herrera" className="mt-1 h-11" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Email</label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@property.com" className="mt-1 h-11" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Phone</label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91" className="mt-1 h-11" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Property type</label>
                <Select value={form.property} onValueChange={v => set('property', v)}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel">Hotel / Resort</SelectItem>
                    <SelectItem value="yacht">Yacht / Superyacht</SelectItem>
                    <SelectItem value="villa">Private Villa</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs text-slate-600 font-medium">Tell us about your project</label>
              <Textarea value={form.message} onChange={e => set('message', e.target.value)} placeholder="Rooms, keys, water demand, timeline…" className="mt-1 min-h-[110px]" />
            </div>
            <Button type="submit" size="lg" className="btn-primary w-full mt-6 h-12 bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white rounded-full text-base shadow-lg shadow-sky-200">
              Request Free Demo <Send className="ml-2 w-4 h-4" />
            </Button>
            <p className="mt-3 text-[11px] text-center text-slate-500">We reply within 24 hours. No spam — ever.</p>
          </form>
        </div>
      </div>
    </section>
  );
}
